import { AbstractMesh, Scene, Vector3 } from '@babylonjs/core';
import { EXTERIOR_FOV } from '@modules/tourdanji/src/Constants';

import { RenderManager } from '../manager';

import { ZEDCamera } from './ZEDCamera';


/**
 * Get the world extend vectors with an optional filter
 *
 * @param filterPredicate the predicate - which meshes should be included when calculating the world size
 * @returns {{ min: Vector3; max: Vector3 }} min and max vectors
 */
export const getWorldExtends = (
  scene: Scene,
  filterPredicate?: (mesh: AbstractMesh) => boolean
): { min: Vector3; max: Vector3 } => {
  const min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
  const max = new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
  filterPredicate = filterPredicate || (() => true);
  scene.meshes.filter(filterPredicate).forEach((mesh) => {
    mesh.computeWorldMatrix(true);

    if (!mesh.subMeshes || mesh.subMeshes.length === 0 || mesh.infiniteDistance) {
      return;
    }

    const boundingInfo = mesh.getBoundingInfo();

    const minBox = boundingInfo.boundingBox.minimumWorld;
    const maxBox = boundingInfo.boundingBox.maximumWorld;

    Vector3.CheckExtends(minBox, min, max);
    Vector3.CheckExtends(maxBox, min, max);
  });

  return {
    min: min,
    max: max,
  };
};

/** Radian To Angle */
export const toAngle = 180 / Math.PI;
/** Angle To Radian */
export const toRadian = Math.PI / 180;

const defaultTarget: Vector3 = Vector3.Zero();
const defaultRadius: number = 3;
const defaultAlpha: number = -90 * toRadian;
const defaultBeta: number = 0;
const defaultFov: number = EXTERIOR_FOV;
const defaultLowerBetaLimit = 2 * toRadian;
const defaultUpperBetaLimit = 90 * toRadian;
const defaultLowerRadiusLimit = 1;

/** 모든 active camera를 제거하고 ZEDCamera를 생성합니다 */
export const createZEDCamera = (scene: Scene, renderManager: RenderManager) => {
  if (scene.activeCamera instanceof ZEDCamera) {
    return scene.activeCamera;
  }

  if (scene.activeCameras) {
    for (let i = scene.activeCameras.length - 1; i >= 0; i--) {
      scene.activeCameras[i]?.dispose();
    }
    if (scene.activeCamera) {
      scene.activeCamera.dispose();
    }
    scene.activeCamera = null;
  }

  if (scene.activeCamera) {
    console.log('active camera가 존재합니다.');
  }

  const worldExtends = getWorldExtends(scene, (mesh) => mesh.isVisible && mesh.isEnabled());
  const worldSize = worldExtends.max.subtract(worldExtends.min);
  const worldCenter = worldExtends.min.add(worldSize.scale(0.5));

  let camera: ZEDCamera;
  let radius = worldSize.length() * 1.5;
  // empty scene scenario!
  if (!isFinite(radius)) {
    radius = 1;
    worldCenter.copyFromFloats(0, 0, 0);
  }
  camera = new ZEDCamera(
    'default camera',
    renderManager,
    defaultTarget,
    defaultAlpha,
    defaultBeta,
    defaultRadius,
    this
  );
  camera.lowerRadiusLimit = radius * 0.01;
  // camera.wheelPrecision = 100 / radius;

  // camera.target = defaultTarget;
  // camera.radius = defaultRadius;
  // camera.alpha = defaultAlpha;
  // camera.beta = defaultBeta;
  camera.fov = defaultFov;

  camera.lowerBetaLimit = defaultLowerBetaLimit;
  camera.upperBetaLimit = defaultUpperBetaLimit;
  camera.lowerRadiusLimit = defaultLowerRadiusLimit;

  camera.inertia = 0.3;
  camera.panningInertia = 0.3;

  camera.minZ = radius * 0.01;
  camera.maxZ = radius * 1000;
  camera.speed = radius * 0.2;

  camera.minZ = 0.05;

  camera.attachControl();

  scene.activeCamera = camera;
  scene.onActiveCameraChanged.notifyObservers(scene);

  return camera;
};

const calculateDistanceToGround = (cameraPosition: Vector3, cameraDirection: Vector3) => {
  if (cameraDirection.y === 0) {
    return undefined;
  }

  const distance = -cameraPosition.y / cameraDirection.y;
  return distance >= 0 ? distance : undefined;
};

/**
 * 카메라를 특정 좌표 기준 영역이 화면에 들어오도록 radius를 계산해 반환합니다
 * @param camera ZEDCamera
 * @param bounds 영역 크기
 * @param fitMargin 영역에 비율로 거리를 조정하는 값
 * @param rotateable true일 시 주어진 영역의 크기가 가로보다 세로가 길다면, 회전합니다
 * @returns radius, rotate(회전 필요 여부)
 */
export const getFitRadius = (
  camera: ZEDCamera,
  bounds: { min: Vector3; max: Vector3 },
  fitMargin: number,
  rotateable: boolean
) => {
  let rotate = false;
  let cloneBounds = {
    min: bounds.min.clone(),
    max: bounds.max.clone(),
  };
  if (rotateable) {
    const isPotrate = window.innerHeight > window.innerWidth;
    const min = cloneBounds.min.clone();
    const max = cloneBounds.max.clone();

    const extX = cloneBounds.max.x - cloneBounds.min.x;
    const extZ = cloneBounds.max.z - cloneBounds.min.z;
    if (isPotrate) {
      if (extX > extZ) {
        const maxX = max.x;
        max.x = max.z;
        max.z = maxX;

        const minX = min.x;
        min.x = min.z;
        min.z = minX;

        rotate = true;
        cloneBounds.max = max;
        cloneBounds.min = min;
      }
    }
  }

  const canvas = camera.getScene().getEngine().getRenderingCanvas();
  if (!canvas) {
    const size = bounds.max.subtract(bounds.min);
    return {
      radius: size.length() * fitMargin,
      rotate: rotate,
    };
  }

  const fovH = camera.fov;
  const fovW = camera.fov * (canvas.width / canvas.height);
  const halfW = (cloneBounds.max.x - cloneBounds.min.x) * 0.5;
  const halfH = (cloneBounds.max.z - cloneBounds.min.z) * 0.5;
  const radiusByW = halfW * (1 / Math.tan(fovW * 0.5));
  const radiusByH = halfH * (1 / Math.tan(fovH * 0.5));

  const radius = Math.max(radiusByW, radiusByH) + (cloneBounds.max.y - cloneBounds.min.y);

  if (camera.upperRadiusLimit === null) {
    const limit = radius * (fitMargin > 1 ? fitMargin : 1) + 1;
    camera.upperRadiusLimit = limit;
    camera.lowerRadiusLimit = limit * 0.01;
  }
  return {
    radius: radius * fitMargin,
    rotate: rotate,
  };
};

/**
 * 오른손 좌표계에서 왼손 좌표계로 변환
 * @param vector 변환할 벡터
 * @param isUnity true일 시 유니티와 같이 추가로 x축을 반전합니다
 * @returns 변환된 벡터
 */
export const convertRightToLeftHanded = (vector: Vector3, isUnity: boolean = false) => {
  return new Vector3(isUnity ? -vector.x : vector.x, vector.y, -vector.z);
};
/**
 * 오른손 좌표계에서 왼손 좌표계로 각도를 변환
 * @param vector 변환할 벡터
 * @returns 변환된 벡터
 */
export const convertRightToLeftHandedRotation = (rotation: Vector3) => {
  return new Vector3(-rotation.x, -rotation.y, rotation.z);
};
